import React from 'react';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';

// ----------------------------------------------------------------------
// About — KBT in its own words. Editorial layout: cinematic hero,
// magazine essay, three-up values, dusk-style donation invitation.
// ----------------------------------------------------------------------

const VALUES = [
  {
    no: '01',
    head: 'Torah & Lifelong Learning',
    body:
      "Torah study is the heartbeat of KBT. We pursue knowledge and wisdom through the holy Torah — education enlightens our paths, enriches our community, and deepens our connection to Hashem.",
  },
  {
    no: '02',
    head: 'Community',
    body:
      "Our strength is rooted in our community. KBT fosters belonging and mutual support — celebrating our shared heritage and the collective energy of every member who carries the kehilla forward.",
  },
  {
    no: '03',
    head: 'Compassion & Tikkun Olam',
    body:
      "Anchored in the teachings of Torah, we live the principles of Tikkun Olam — engaging in kindness, tzedakah, and social justice to leave a more equitable world for future generations.",
  },
];

export default function About() {
  return (
    <EditorialPageShell
      title="About"
      description="Kehilat Bnei Torah — a vibrant Los Angeles kehilla rooted in daily Torah, prayer, and chesed."
    >
      <PageHero
        kicker="About  ·  KBT"
        meta="Est. in Los Angeles"
        title="A kehilla rooted"
        titleAccent="in Torah."
        subtitle="Kehilat Bnei Torah is more than a Beit Knesset. It is a daily beit midrash, an open table, and a movement to bring a million hours of Torah back to Los Angeles."
        image="/assets/images/kbt/shiur.jpg"
        height="84vh"
      />

      {/* Essay */}
      <EditorialSection
        kicker="Our Essence"
        title="More than a congregation —"
        titleAccent="a living, learning kehilla."
        watermark="ת"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
            gap: { xs: 6, md: 10 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              className="drop-cap"
              sx={{
                fontFamily: 'var(--font-body)',
                fontSize: { xs: 17, md: 19 },
                lineHeight: 1.7,
                color: 'var(--text)',
              }}
            >
              Kehilat Bnei Torah is a vibrant community anchored in the rich
              traditions of the Torah. We are a dynamic kehilla, connected by
              our shared commitment to Torah study, prayer, and the enduring
              values that have shaped our heritage for generations. Our mission
              is to foster a robust Jewish community, deeply rooted in the
              teachings of the Torah, in the heart of Los Angeles.
            </Box>
          </motion.div>

          <Stack spacing={3.5}>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--teal-500)',
              }}
            >
              Our Purpose
            </Box>
            <Box
              sx={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: { xs: 24, md: 30 },
                lineHeight: 1.2,
                color: 'var(--navy-500)',
                letterSpacing: '-0.01em',
              }}
            >
              Through daily morning and nightly learning, comprehensive
              educational programs, and a warm, welcoming environment.
            </Box>
            <Box
              sx={{
                fontFamily: 'var(--font-body)',
                fontSize: 16.5,
                lineHeight: 1.7,
                color: 'var(--text)',
                opacity: 0.86,
              }}
            >
              KBT offers a sanctuary where individuals and families of every
              background can explore, learn, and find spiritual fulfillment as
              we journey together on a meaningful path.
            </Box>
          </Stack>
        </Box>
      </EditorialSection>

      {/* Values */}
      <EditorialSection
        background="var(--paper-warm)"
        kicker="Our Core Values"
        title="Three commitments"
        titleAccent="that hold the kehilla together."
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 5, md: 6 },
          }}
        >
          {VALUES.map((v, i) => (
            <motion.div
              key={v.no}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box
                sx={{
                  borderTop: '1px solid rgba(36,34,88,0.18)',
                  pt: 4,
                  pr: 1,
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    letterSpacing: '0.22em',
                    color: 'var(--teal-500)',
                    mb: 2,
                  }}
                >
                  {v.no}  /  Value
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: { xs: 26, md: 30 },
                    lineHeight: 1.1,
                    letterSpacing: '-0.01em',
                    color: 'var(--navy-500)',
                    mb: 2.5,
                  }}
                >
                  {v.head}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 16,
                    lineHeight: 1.7,
                    color: 'var(--text)',
                    opacity: 0.85,
                  }}
                >
                  {v.body}
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>
      </EditorialSection>

      {/* Dusk invitation */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          background: 'var(--navy-900)',
          color: '#fff',
          overflow: 'hidden',
          py: { xs: 10, md: 14 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(7,6,26,0.7) 0%, rgba(7,6,26,0.85) 100%), url('/assets/images/kbt/night-seder.jpg') center/cover no-repeat",
            opacity: 0.55,
          }}
        />
        <Box
          sx={{
            position: 'relative',
            maxWidth: 1100,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 4, md: 8 },
          }}
        >
          <Box sx={{ maxWidth: 620 }}>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--teal-300)',
                mb: 2,
              }}
            >
              Help us build
            </Box>
            <Box
              sx={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: { xs: 30, md: 44 },
                lineHeight: 1.05,
                letterSpacing: '-0.01em',
                color: '#fff',
              }}
            >
              Empower our journey.{' '}
              <span style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--teal-300)' }}>
                Sustain the kehilla.
              </span>
            </Box>
          </Box>
          <EditorialButton href="/donate-now" variant="inverse">
            Donate Now
          </EditorialButton>
        </Box>
      </Box>
    </EditorialPageShell>
  );
}
